// src/services/dataPersistence/transformers.js
/**
 * Centralized data transformation utilities for bidirectional conversions
 * between client-side (localStorage) format and server-side (API/database) format.
 * 
 * This ensures consistent data structure throughout the application and 
 * preserves important attributes like IDs and relationships.
 */

/**
 * ===================================
 * HELPER UTILITY FUNCTIONS
 * ===================================
 */

/**
 * Generates a timestamp string in ISO format
 */
const generateTimestamp = () => new Date().toISOString();

/**
 * Checks if a value is a valid date string or Date object
 */
/* const isValidDate = (date) => {
  if (!date) return false;
  if (date instanceof Date) return !isNaN(date);
  
  // If it's a string, try to convert to Date
  const d = new Date(date);
  return !isNaN(d);
}; */
const isValidDate = (date: any) => {
  if (!date) return false;
  if (date instanceof Date) return !isNaN(+date); // Convert Date to number
  
  // If it's a string, try to convert to Date
  const d = new Date(date);
  return !isNaN(+d); // Convert Date to number
};

/**
 * Converts any date to ISO string format (with safeguards)
 */
const toISOString = (date:any) => {
  if (!date) return null;
  if (date instanceof Date) return date.toISOString();
  if (typeof date === 'string') {
    try {
      return new Date(date).toISOString();
    } catch (e) {
      return date; // Return original if can't convert
    }
  }
  return null;
};

/**
 * Deep clone an object to avoid reference issues
 */
const deepClone = (obj:any) => {
  if (obj === null || typeof obj !== 'object') return obj;
  return JSON.parse(JSON.stringify(obj));
};

/**
 * ===================================
 * CONVERSATION TRANSFORMERS
 * ===================================
 */

/**
 * Transform conversation data from client to API format
 */

interface ConversationClientData {
  id?: string | null;
  sequence?: any[];
  lastScrollPosition?: number;
  scrollPosition?: number;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

function conversationClientToApi(clientData: ConversationClientData) {
  if (!clientData) return null;
  
  // Create a deep copy to avoid mutations
  const data = deepClone(clientData);

  //console.log('[Transformer] Single conversationClientToApi:', clientData);
  
  return {
    id: data.id || null,
    sequence: data.sequence || [],
    scrollPosition: data.lastScrollPosition || data.scrollPosition || 0, // Map lastScrollPosition → scrollPosition
    createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
    updatedAt: data.updatedAt ? new Date(data.updatedAt) : new Date()
  };
}

/**
 * Transform conversation data from API to client format
 */
function conversationApiToClient(apiData:any) {
  if (!apiData) return null;
  
  // Create a deep copy to avoid mutations
  const data = deepClone(apiData);
  
  //console.log('[Transformer] Single conversationApiToClient:', apiData);

  return {
    id: data.id || null,
    sequence: data.sequence || [],
    lastScrollPosition: data.scrollPosition || 0, // Map scrollPosition → lastScrollPosition
    createdAt: data.createdAt ? toISOString(data.createdAt) : generateTimestamp(),
    updatedAt: data.updatedAt ? toISOString(data.updatedAt) : generateTimestamp()
  };
}

/**
 * ===================================
 * LOCATION TRANSFORMERS
 * ===================================
 */

/**
 * Transform location data from client to API format
 */

function locationClientToApi(clientData:any) {
  if (!clientData) return null;
  
  const data = deepClone(clientData);
  
  let lat = 0;
  let lng = 0;
  
  if (data.position && typeof data.position === 'object') {
    lat = Number(data.position.lat);
    lng = Number(data.position.lng);
    
    if (isNaN(lat)) lat = 0;
    if (isNaN(lng)) lng = 0;
  }

  /* console.log('[Transformer - locationApiToClient]', {
    name: clientData.name,
    originalPosition: clientData.position,
    afterNumber: {
      lat: Number(clientData.position.lat),
      lng: Number(clientData.position.lng)
    },
    willSaveAs: {
      latitude: lat,
      longitude: lng
    }
  }); */
  
  return {
    id: data.id,
    name: data.name || 'Unnamed Location',
    city: data.city || 'Unknown City',
    country: data.country || 'Unknown Country',
    latitude: lat,
    longitude: lng,
    rating: data.rating || 0,
    reviews: data.reviews || 0,
    description: data.description || '',
    timestamp: data.timestamp ? new Date(data.timestamp) : new Date(),
    // Add new fields
    type: data.type,
    types: data.types || null, // Add this
    cuisine: data.cuisine,
    priceLevel: data.priceLevel,
    formattedAddress: data.formattedAddress,
    businessStatus: data.businessStatus,
    placeId: data.placeId
  };
}


function locationApiToClient(apiData:any) {
  if (!apiData) return null;

  //console.log('[Transformer] locationApiToClient:', apiData.latitude, apiData.longitude);

  
  return {
    id: apiData.id || null,
    name: apiData.name || 'Unnamed Location',
    city: apiData.city || 'Unknown City',
    country: apiData.country || 'Unknown Country',
    position: {
      lat: apiData.latitude,
      lng: apiData.longitude
    },
    rating: apiData.rating || 0,
    reviews: apiData.reviews || 0,
    description: apiData.description || '',
    timestamp: apiData.timestamp ? toISOString(apiData.timestamp) : generateTimestamp(),
    // Add ALL the new fields:
    type: apiData.type || null,
    cuisine: apiData.cuisine || null,
    priceLevel: apiData.priceLevel ?? null, // Use ?? for numbers
    formattedAddress: apiData.formattedAddress || null,
    businessStatus: apiData.businessStatus || null,
    placeId: apiData.placeId || null,
    types: apiData.types || null
  };
}


function locationsApiToClient(apiLocations:any) {
  if (!apiLocations || !Array.isArray(apiLocations)) return [];
  
  //console.log('[Transformer] Processing locations:', apiLocations.length);
  
  // Check the actual structure
  if (apiLocations.length > 0) {
    const sample = apiLocations[0];
    /* console.log('[DEBUG] Sample properties:', Object.keys(sample));
    console.log('[DEBUG] Position structure:', sample.position || 
                (sample.latitude !== undefined ? {lat: sample.latitude, lng: sample.longitude} : 'Missing')); */
  }
  
  return apiLocations.map(loc => {
    // If data is already in client format, return it directly
    if (loc.position) return loc;
    
    // Otherwise transform it
    return locationApiToClient(loc);
  });
}  

/**
 * Transform multiple location cards at once
 */

 function locationsClientToApi(clientLocations:any) {
  if (!clientLocations || !Array.isArray(clientLocations)) return [];
  return clientLocations.map(loc => locationClientToApi(loc));
} 

/* function locationsApiToClient(apiLocations) {
  if (!apiLocations || !Array.isArray(apiLocations)) return [];
  return apiLocations.map(loc => locationApiToClient(loc));
} */

/**
 * ===================================
 * WEATHER TRANSFORMERS
 * ===================================
 */

/**
 * Transform weather data from client to API format
 */
function weatherClientToApi(clientData:any) {
  if (!clientData) return null;
  
  // Create a deep copy to avoid mutations
  const data = deepClone(clientData);
  
  // Extract city and country from location if possible
  let city = 'Unknown';
  let country = 'Unknown';
  
  if (data.location && typeof data.location === 'string') {
    const parts = data.location.split(',').map((p: string) => p.trim());
    if (parts.length >= 1) city = parts[0];
    if (parts.length >= 2) country = parts[1];
  }

  //console.log('[Transformer] weatherClientToApi:', data);
  
  return {
    id: data.id || null, // Preserve the original ID
    location: data.location || 'Unknown',
    city,
    country,
    temperature: data.temperature,
    condition: data.condition,
    forecast: data.forecast || [],
    timestamp: data.timestamp ? new Date(data.timestamp) : new Date(),
    // Critical: Preserve the messageId relationship
    messageId: data.messageId
  };
}

/**
 * Transform weather data from API to client format
 */
function weatherApiToClient(apiData:any) {
  if (!apiData) return null;
  
  // Create a deep copy to avoid mutations
  const data = deepClone(apiData);

  //console.log('[Transformer] weatherApiToClient:', data);
  
  return {
    id: data.id || null, // Preserve the original ID
    location: data.location || `${data.city || 'Unknown'}, ${data.country || 'Unknown'}`,
    temperature: data.temperature,
    condition: data.condition,
    forecast: data.forecast || [],
    timestamp: data.timestamp ? toISOString(data.timestamp) : generateTimestamp(),
    // Preserve the messageId relationship if it exists
    ...(data.messageId ? { messageId: data.messageId } : {})
  };
}

/**
 * Transform multiple weather widgets at once
 */
function weatherWidgetsClientToApi(clientWeatherWidgets:any) {
  if (!clientWeatherWidgets || !Array.isArray(clientWeatherWidgets)) return [];

  //console.log('[Transformer] weatherWidgetsClientToApi:', clientWeatherWidgets);

  return clientWeatherWidgets.map(widget => weatherClientToApi(widget));
}

function weatherWidgetsApiToClient(apiWeatherWidgets:any) {
  if (!apiWeatherWidgets || !Array.isArray(apiWeatherWidgets)) return [];
  return apiWeatherWidgets.map(widget => weatherApiToClient(widget));
}

/**
 * ===================================
 * MAP STATE TRANSFORMERS
 * ===================================
 */

/**
 * Transform map state from client to API format
 */
function mapStateClientToApi(clientData:any) {
  if (!clientData) return null;
  
  //console.log('[Transformer] mapStateClientToApi:', clientData);

  // Create a deep copy to avoid mutations
  const data = deepClone(clientData);
  
  return {
    id: data.id || null,
    centerLat: data.center?.lat || 0, // Map center.lat → centerLat
    centerLng: data.center?.lng || 0, // Map center.lng → centerLng
    zoom: data.zoom || 2,
    visibleLocationIds: data.visibleLocationIds || [], // Ensure array preservation
    selectedLocationId: data.selectedLocationId || null,
    updatedAt: data.updatedAt ? new Date(data.updatedAt) : new Date()
  };
}

/**
 * Transform map state from API to client format
 */
function mapStateApiToClient(apiData:any) {
  if (!apiData) return null;
  
  // Create a deep copy to avoid mutations
  const data = deepClone(apiData);
  
  return {
    id: data.id || null,
    center: { // Map centerLat/centerLng → center.lat/lng
      lat: data.centerLat || 0,
      lng: data.centerLng || 0
    },
    zoom: data.zoom || 2,
    visibleLocationIds: data.visibleLocationIds || [], // Ensure array preservation
    selectedLocationId: data.selectedLocationId || null,
    updatedAt: data.updatedAt ? toISOString(data.updatedAt) : generateTimestamp()
  };
}

/**
 * ===================================
 * ITINERARY TRANSFORMERS
 * ===================================
 */

/**
 * Transform itinerary data from client to API format
 */

function itineraryClientToApi(clientData:any) {
  if (!clientData) return null;
  
  // Create a deep copy to avoid mutations
  const data = deepClone(clientData);
  
  // Ensure tripDetails exists and has the correct structure
  const tripDetails = {
    destination: data.tripDetails?.destination || 'Unknown Destination',
    title: data.tripDetails?.destination || 'Unknown Destination', // Include title as a copy of destination
    startDate: data.tripDetails?.startDate ? new Date(data.tripDetails.startDate) : new Date(),
    endDate: data.tripDetails?.endDate ? new Date(data.tripDetails.endDate) : new Date(),
    travelGroup: data.tripDetails?.travelGroup || 'Solo traveler'
  };
  
  // Define an interface for the day object
  interface DayData {
    dayNumber?: number;
    date?: string;
    activities?: any[];
  }
  // Ensure days is an array with at least one item if undefined
  const days = Array.isArray(data.days) && data.days.length > 0 
    ? data.days.map((day: DayData) => ({
        dayNumber: day.dayNumber || 1,
        date: day.date || new Date().toISOString(),
        activities: Array.isArray(day.activities) ? day.activities : []
      }))
    : [{
        dayNumber: 1,
        date: new Date().toISOString(),
        activities: []
      }];
  
  //console.log('[TRANSFORM] Budget Before:', clientData.budgetSummary);
        
  // Handle budgetSummary with proper defaults
  const budgetSummary = clientData.budgetSummary ? {
    totalEstimatedBudget: clientData.budgetSummary.totalEstimatedBudget || "$0",
    categoryBreakdown: {
      attractions: clientData.budgetSummary.categoryBreakdown?.attractions || "$0",
      foodAndDining: clientData.budgetSummary.categoryBreakdown?.foodAndDining || "$0",
      transportation: clientData.budgetSummary.categoryBreakdown?.transportation || "$0",
      shoppingAndMisc: clientData.budgetSummary.categoryBreakdown?.shoppingAndMisc || "$0",
      buffer: clientData.budgetSummary.categoryBreakdown?.buffer || "$0"
    }
  } : null; // Only null if explicitly null, not undefined
  
  // Log budget data to debug
  /* console.log('[DEBUG] Budget data being transformed:', 
    { original: data.budgetSummary, transformed: budgetSummary }); */
  
  //console.log('[TRANSFORM] Budget After:', budgetSummary);    
  
  return {
    id: data.id || null,
    tripDetails,
    days,
    budgetSummary,
    createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
    updatedAt: data.updatedAt ? new Date(data.updatedAt) : new Date()
  };
}
  /**
 * Transform itinerary data from API to client format
 */
function itineraryApiToClient(apiData:any) {
  if (!apiData) return null;
  
  // Create a deep copy to avoid mutations
  const data = deepClone(apiData);
  
  // Handle both flat and nested structures for backward compatibility
  const tripDetails = data.tripDetails || {};
  
  return {
    id: data.id || null, // Preserve original ID
    tripDetails: {
      destination: tripDetails.destination || data.destination || 'Unknown Destination',
      startDate: tripDetails.startDate 
        ? toISOString(tripDetails.startDate) 
        : (data.startDate ? toISOString(data.startDate) : generateTimestamp()),
      endDate: tripDetails.endDate 
        ? toISOString(tripDetails.endDate) 
        : (data.endDate ? toISOString(data.endDate) : generateTimestamp()),
      travelGroup: tripDetails.travelGroup || data.travelGroup || 'solo'
    },
    days: data.days || [],
    budgetSummary: data.budgetSummary || null,
    createdAt: data.createdAt ? toISOString(data.createdAt) : generateTimestamp(),
    updatedAt: data.updatedAt ? toISOString(data.updatedAt) : generateTimestamp()
  };
}

/**
 * Transform multiple itineraries at once
 */
function itinerariesClientToApi(clientItineraries:any) {
  if (!clientItineraries || !Array.isArray(clientItineraries)) return [];
  return clientItineraries.map(itinerary => itineraryClientToApi(itinerary));
}

function itinerariesApiToClient(apiItineraries:any) {
  if (!apiItineraries || !Array.isArray(apiItineraries)) return [];
  return apiItineraries.map(itinerary => itineraryApiToClient(itinerary));
}

/**
 * ===================================
 * PREFERENCES TRANSFORMERS
 * ===================================
 */

/**
 * Transform preferences from client to API format
 */
function preferencesClientToApi(clientData:any) {
  if (!clientData) return null;
  
  // Create a deep copy to avoid mutations
  const data = deepClone(clientData);
  
  return {
    id: data.id || null,
    uiMode: data.uiMode || 'light',
    mapPreference: data.mapPreference || 'satellite',
    otherPrefs: data.customSettings || {},
    updatedAt: data.updatedAt ? new Date(data.updatedAt) : new Date()
  };
}

/**
 * Transform preferences from API to client format
 */
function preferencesApiToClient(apiData:any) {
  if (!apiData) return null;
  
  // Create a deep copy to avoid mutations
  const data = deepClone(apiData);
  
  return {
    id: data.id || null,
    uiMode: data.uiMode || 'light',
    mapPreference: data.mapPreference || 'satellite',
    customSettings: data.otherPrefs || {},
    updatedAt: data.updatedAt ? toISOString(data.updatedAt) : generateTimestamp()
  };
}

/**
 * ===================================
 * IMAGE MESSAGE TRANSFORMERS
 * ===================================
 */

/**
 * Transform image message from client to API format
 */
function imageClientToApi(clientData:any) {
  if (!clientData) return null;
  
  // Create a deep copy to avoid mutations
  const data = deepClone(clientData);
  
  return {
    id: data.id || null, // Preserve original ID
    content: data.content || '',
    sender: data.sender || 'user',
    timestamp: data.timestamp ? new Date(data.timestamp) : new Date(),
    imageUrl: data.imageUrl || ''
  };
}

/**
 * Transform image message from API to client format
 */
function imageApiToClient(apiData:any) {
  if (!apiData) return null;
  
  // Create a deep copy to avoid mutations
  const data = deepClone(apiData);
  
  return {
    id: data.id || null, // Preserve original ID
    content: data.content || '',
    sender: data.sender || 'user',
    timestamp: data.timestamp ? toISOString(data.timestamp) : generateTimestamp(),
    imageUrl: data.imageUrl || ''
  };
}

/**
 * Transform multiple image messages at once
 */
function imagesClientToApi(clientImages:any) {
  if (!clientImages || !Array.isArray(clientImages)) return [];
  return clientImages.map(image => imageClientToApi(image));
}

function imagesApiToClient(apiImages:any) {
  if (!apiImages || !Array.isArray(apiImages)) return [];
  return apiImages.map(image => imageApiToClient(image));
}

// Export all transformers
export default {
  // Conversation transformers
  conversationClientToApi,
  conversationApiToClient,
  
  // Location transformers
  locationClientToApi,
  locationApiToClient,
  locationsClientToApi,
  locationsApiToClient,
  
  // Weather transformers
  weatherClientToApi,
  weatherApiToClient,
  weatherWidgetsClientToApi,
  weatherWidgetsApiToClient,
  
  // Map state transformers
  mapStateClientToApi,
  mapStateApiToClient,
  
  // Itinerary transformers
  itineraryClientToApi,
  itineraryApiToClient,
  itinerariesClientToApi,
  itinerariesApiToClient,
  
  // Preferences transformers
  preferencesClientToApi,
  preferencesApiToClient,
  
  // Image message transformers
  imageClientToApi,
  imageApiToClient,
  imagesClientToApi,
  imagesApiToClient,
  
  // Utility functions
  generateTimestamp,
  isValidDate,
  toISOString,
  deepClone
};